import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DEVICE_STATUS_LABELS, DeviceStatus } from '@fleet/shared';
import { telematicsApi, type TelematicsDeviceView } from '../../api/telematics';
import { AsyncBoundary } from '../../ui/AsyncBoundary';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { DataTable } from '../../ui/DataTable';
import { Modal } from '../../ui/Modal';
import { formatInstant } from '../../ui/datetime';
import { DeviceAssignmentDialog } from './DeviceAssignmentDialog';
import { DeviceForm } from './DeviceForm';

/**
 * Geräte und ihre Fahrzeuge.
 *
 * Gerätezentrisch und nicht als Feld am Fahrzeug, weil der Dongle das
 * wandernde Teil ist: Umstecken ist ein Vorgang mit zwei Fahrzeugen und einem
 * Zeitpunkt, und in zwei getrennten Fahrzeugformularen wäre er nicht als einer
 * darstellbar.
 *
 * Für Administration und Fuhrparkleitung — anders als die Rohdatenansicht, wo
 * unredigierte Positionen stehen. Hier steht nur, welches Gerät in welchem
 * Fahrzeug sitzt.
 */
export function DevicesPage() {
  const [registering, setRegistering] = useState(false);
  const [assigning, setAssigning] = useState<TelematicsDeviceView | null>(null);
  const [ending, setEnding] = useState<TelematicsDeviceView | null>(null);
  const [historyFor, setHistoryFor] = useState<TelematicsDeviceView | null>(null);

  const query = useQuery({ queryKey: ['telematics-devices'], queryFn: telematicsApi.devices });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Geräte</h2>
          <p className="text-sm text-slate-500">
            Welcher Datenlogger in welchem Fahrzeug sitzt. Die Zuordnung gilt ab einem Zeitpunkt —
            daraus wird später abgeleitet, welchem Fahrzeug eine Fahrt gehört.
          </p>
        </div>
        <Button variant="primary" onClick={() => setRegistering(true)}>
          Gerät registrieren
        </Button>
      </div>

      <AsyncBoundary
        query={query}
        empty="Noch kein Gerät eingetragen. Ohne registrierte IMEI weist der Empfang jedes Gerät ab."
      >
        {(devices) => (
          <DataTable
            rows={devices}
            rowKey={(device) => device.id}
            columns={[
              {
                header: 'IMEI',
                cell: (device) => <span className="font-mono text-xs">{device.imei}</span>,
              },
              {
                header: 'Fahrzeug',
                cell: (device) =>
                  device.currentAssignment ? (
                    <div>
                      <div className="font-medium">
                        {device.currentAssignment.vehicle.licensePlate}
                      </div>
                      <div className="text-xs text-slate-500">
                        {device.currentAssignment.vehicle.displayName} · seit{' '}
                        {formatInstant(device.currentAssignment.validFrom)}
                      </div>
                    </div>
                  ) : (
                    <span className="text-slate-400">nicht zugeordnet</span>
                  ),
              },
              { header: 'Modell', cell: (device) => device.model },
              { header: 'Status', cell: (device) => <DeviceState status={device.status} /> },
              {
                header: 'Zuletzt gesehen',
                cell: (device) =>
                  device.lastSeenAt ? (
                    <span className="tabular-nums text-xs">
                      {formatInstant(device.lastSeenAt, 'medium')}
                    </span>
                  ) : (
                    <span className="text-slate-400">nie</span>
                  ),
              },
              {
                header: '',
                align: 'right',
                cell: (device) => (
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setHistoryFor(device)}>
                      Verlauf
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setAssigning(device)}>
                      {device.currentAssignment ? 'Umstecken' : 'Einbauen'}
                    </Button>
                    {device.currentAssignment && (
                      <Button size="sm" variant="ghost" onClick={() => setEnding(device)}>
                        Ausbauen
                      </Button>
                    )}
                  </div>
                ),
              },
            ]}
          />
        )}
      </AsyncBoundary>

      {registering && <DeviceForm onClose={() => setRegistering(false)} />}
      {assigning && (
        <DeviceAssignmentDialog
          device={assigning}
          mode="assign"
          onClose={() => setAssigning(null)}
        />
      )}
      {ending && (
        <DeviceAssignmentDialog device={ending} mode="end" onClose={() => setEnding(null)} />
      )}
      {historyFor && <HistoryDialog device={historyFor} onClose={() => setHistoryFor(null)} />}
    </div>
  );
}

function DeviceState({ status }: { status: string }) {
  const label = DEVICE_STATUS_LABELS[status as DeviceStatus] ?? status;

  if (status === DeviceStatus.ACTIVE) return <Badge tone="emerald">{label}</Badge>;
  if (status === DeviceStatus.DEFECT) return <Badge tone="red">{label}</Badge>;
  if (status === DeviceStatus.STOCK) return <Badge tone="amber">{label}</Badge>;
  return <Badge tone="neutral">{label}</Badge>;
}

/**
 * Der Verlauf, nicht nur der Ist-Zustand.
 *
 * „In welchem Fahrzeug steckte das Gerät im Juli" ist genau die Frage, die beim
 * Prüfen eines Fahrtenbuchs gestellt wird. Ohne diese Ansicht wäre sie nur über
 * die Datenbank zu beantworten.
 */
function HistoryDialog({ device, onClose }: { device: TelematicsDeviceView; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['device-assignments', device.id],
    queryFn: () => telematicsApi.assignments(device.id),
  });

  return (
    <Modal title={`Verlauf ${device.imei}`} onClose={onClose} size="lg">
      <AsyncBoundary query={query} empty="Dieses Gerät war noch keinem Fahrzeug zugeordnet.">
        {(rows) => (
          <DataTable
            rows={rows}
            rowKey={(row) => row.id}
            columns={[
              {
                header: 'Fahrzeug',
                cell: (row) => (
                  <div>
                    <div className="font-medium">{row.vehicle.licensePlate}</div>
                    <div className="text-xs text-slate-500">{row.vehicle.displayName}</div>
                  </div>
                ),
              },
              { header: 'Von', cell: (row) => formatInstant(row.validFrom) },
              {
                header: 'Bis',
                cell: (row) =>
                  row.validTo ? formatInstant(row.validTo) : <Badge tone="emerald">montiert</Badge>,
              },
              {
                header: 'Notiz',
                cell: (row) => <span className="text-xs text-slate-500">{row.note ?? '—'}</span>,
              },
            ]}
          />
        )}
      </AsyncBoundary>
    </Modal>
  );
}
